// Export components
export { MarianaWidget } from './components/widget/mariana-widget.js';
export { MarianaChat } from './components/chat/mariana-chat.js';
export { MarianaButtonsNormal } from './components/buttons/mariana-normal-buttons.js';
export { MarianaHierarchyButtons } from './components/buttons/mariana-hierarchy-buttons.js';
export { MarianaListButton } from './components/buttons/mariana-list-button.js';
export { MarianaCsatButtons } from './components/buttons/mariana-csat-buttons.js';
export { MarianaImages } from './components/cards/mariana-images.js';

// Export service
export { ChatService, chatService } from './services/chat-service.js';
export type { ButtonOption, MessageData } from './services/types.js';

// Import components to register them
import './components/widget/mariana-widget.js';
import './components/chat/mariana-chat.js';
import './components/buttons/mariana-normal-buttons.js';

// Types
interface MarianaWidgetConfig {
  source?: string;
}

type WidgetAction = 'showWebChat' | 'hideWebChat' | 'minimizeWebChat' | 'disposeOfferHelpMessage' | 'offerHelpMessage' | 'setMarianaTheme';

type WidgetDragAction = 'setDragLimits' | 'removeDrag';

/**
 * Programmatic API for initializing the widget
 */
const MarianaWidgetAPI = {
  /**
   * Initialize and mount the widget
   */
  init(config: MarianaWidgetConfig = {}) {
    // Check if widget already exists
    let widget: Element | null = document.querySelector('mariana-widget');

    if (!widget) {
      // Create widget element
      widget = document.createElement('mariana-widget');
      document.body.appendChild(widget);
    }

    if (config.source) {
      widget.setAttribute('source', config.source);
    }

    return widget;
  },

  /**
   * Remove the widget from the DOM
   */
  destroy() {
    const widget = document.querySelector('mariana-widget');
    if (widget) {
      widget.remove();
    }
  },

  /**
   * Get the widget instance
   */
  getInstance() {
    return document.querySelector('mariana-widget');
  },

  showWebChat() {
    return invokeWidgetAction('showWebChat');
  },

  setMarianaTheme(theme: 'mariana-dark-mode' | 'mariana-light-mode' | undefined) {
    return invokeWidgetAction('setMarianaTheme', theme);
  },

  hideWebChat() {
    return invokeWidgetAction('hideWebChat');
  },

  minimizeWebChat() {
    return invokeWidgetAction('minimizeWebChat');
  },

  offerHelpMessage() {
    return invokeWidgetAction('offerHelpMessage');
  },

  disposeOfferHelpMessage() {
    return invokeWidgetAction('disposeOfferHelpMessage');
  },

  setDragLimits(right: number, bottom: number) {
    return invokeWidgetDragAction('setDragLimits', right, bottom);
  },

  removeDrag() {
    return invokeWidgetDragAction('removeDrag');
  },
};

function getOrCreateWidgetInstance() {
  const existing = MarianaWidgetAPI.getInstance();
  if (existing) {
    return existing;
  }

  return MarianaWidgetAPI.init();
}


function invokeWidgetAction(action: WidgetAction, ...args: any[]): boolean {
  try {
    const widget = getOrCreateWidgetInstance() as HTMLElement & Record<WidgetAction, ((...args: any[]) => void) | undefined>;
    const actionFn = widget[action];
    if (typeof actionFn === 'function') {
      actionFn.call(widget, ...args);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

function invokeWidgetDragAction(action: WidgetDragAction, right?: number, bottom?: number): boolean {
  try {
    const widget = getOrCreateWidgetInstance() as HTMLElement & {
      setDragLimits?: (right: number, bottom: number) => void;
      removeDrag?: () => void;
    };

    if (action === 'setDragLimits') {
      if (typeof widget.setDragLimits === 'function') {
        widget.setDragLimits(right ?? 0, bottom ?? 0);
        return true;
      }
    }

    if (action === 'removeDrag') {
      if (typeof widget.removeDrag === 'function') {
        widget.removeDrag();
        return true;
      }
    }
    return false;
  } catch (error) {
    return false;
  }
}

// Expose global API
if (globalThis.window !== undefined) {
  const windowApi = globalThis as unknown as {
    MarianaWidget: typeof MarianaWidgetAPI;
    showWebChat: () => boolean;
    hideWebChat: () => boolean;
    minimizeWebChat: () => boolean;
    offerHelpMessage: () => boolean;
    disposeOfferHelpMessage: () => boolean;
    setDragLimits: (right: number, bottom: number) => boolean;
    removeDrag: () => boolean;
    setMarianaTheme: (theme: 'mariana-dark-mode' | 'mariana-light-mode' | undefined) => boolean;
  };

  windowApi.MarianaWidget = MarianaWidgetAPI;
  windowApi.showWebChat = () => MarianaWidgetAPI.showWebChat();
  windowApi.hideWebChat = () => MarianaWidgetAPI.hideWebChat();
  windowApi.minimizeWebChat = () => MarianaWidgetAPI.minimizeWebChat();
  windowApi.offerHelpMessage = () => MarianaWidgetAPI.offerHelpMessage();
  windowApi.disposeOfferHelpMessage = () => MarianaWidgetAPI.disposeOfferHelpMessage();
  windowApi.setDragLimits = (right: number, bottom: number) => MarianaWidgetAPI.setDragLimits(right, bottom);
  windowApi.removeDrag = () => MarianaWidgetAPI.removeDrag();
  windowApi.setMarianaTheme = (theme: 'mariana-dark-mode' | 'mariana-light-mode' | undefined) => MarianaWidgetAPI.setMarianaTheme(theme);

}

export { MarianaWidgetAPI };
export default MarianaWidgetAPI;
